import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AuthContext,
  type AuthContextValue,
  type LoginResponse,
  type MfaVerifyResponse,
  type SessionUser,
} from '@/lib/auth'
import { ApiError, api, getToken, setToken, setUnauthorizedHandler } from '@/lib/api'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading')
  const [error, setError] = useState<string | null>(null)

  const loadMe = useCallback(async () => {
    const token = getToken()
    if (!token) {
      setUser(null)
      setStatus('unauthenticated')
      return
    }
    try {
      const me = await api.get<SessionUser>('/api/me')
      setUser(me)
      setStatus('authenticated')
    } catch {
      setUser(null)
      setStatus('unauthenticated')
      setToken(null)
    }
  }, [])

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null)
      setStatus('unauthenticated')
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    void loadMe()
  }, [loadMe])

  const login = useCallback<AuthContextValue['login']>(
    async (email, password) => {
      setError(null)
      try {
        const res = await api.post<LoginResponse>(
          '/api/auth/login',
          { email, password },
          { anonymous: true },
        )
        if (res.token && !res.requiresMfa) {
          setToken(res.token)
          await loadMe()
        }
        return res
      } catch (err) {
        const message =
          err instanceof ApiError && err.body?.error === 'invalid_credentials'
            ? 'Credenciales inválidas'
            : err instanceof ApiError
              ? 'No se pudo iniciar sesión'
              : 'Error de conexión'
        setError(message)
        throw err
      }
    },
    [loadMe],
  )

  const verifyMfa = useCallback<AuthContextValue['verifyMfa']>(
    async (mfaChallenge, code) => {
      setError(null)
      try {
        const res = await api.post<MfaVerifyResponse>(
          '/api/auth/mfa/verify',
          { mfaChallenge, code },
          { anonymous: true },
        )
        setToken(res.token)
        await loadMe()
      } catch (err) {
        const message =
          err instanceof ApiError && err.body?.error === 'invalid_mfa'
            ? 'Código de verificación inválido'
            : 'No se pudo verificar el código'
        setError(message)
        throw err
      }
    },
    [loadMe],
  )

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    setStatus('unauthenticated')
    setError(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, error, login, verifyMfa, logout, refresh: loadMe }),
    [user, status, error, login, verifyMfa, logout, loadMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
